import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Row, Col, Card, Skeleton, Typography } from 'antd';
import StackedAreaChart from '../../components/StackedAreaChart';
import { CLOUD_SPEND_CHART_SERIES, CLOUD_SPEND_TIME_SERIES } from '../../utils/chartTimeSeries';
import { formatMoney } from '../../utils/formatMoney';
import { getUsageLogs } from '../../services/api/statistics';
import { useDashboard } from './DashboardContext';
import DashboardFilterBar from './DashboardFilterBar';
import DashboardStatCard from './DashboardStatCard';
import UsageLogTable from './UsageLogTable';
import ModelDistributionTable from './ModelDistributionTable';
import SpendPieChart from '../../components/SpendPieChart';
import { formatPercentageChange } from './dashboardUtils';

const { Text } = Typography;
const LOG_PAGE_SIZE = 20;

const CHANNEL_SPEND_KEYS = [
  { key: 'api_cost_usd', label: 'API' },
  { key: 'bot_cost_usd', label: 'Bot' },
  { key: 'widget_cost_usd', label: 'Widget' },
  { key: 'platform_cost_usd', label: 'Platform' },
];

export default function DashboardSpend() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const channel = searchParams.get('channel');
  const model = searchParams.get('model');

  const {
    keycloak,
    showAll,
    isAdmin,
    timeRange,
    showCloudSpend,
    statistics,
    loading,
    cloudSpendChart,
    modelDistribution,
    spendByAssistant,
  } = useDashboard();

  const platformFlag = showAll && isAdmin;
  const [logPage, setLogPage] = useState(1);
  const [usageLogs, setUsageLogs] = useState([]);
  const [usageLogsTotal, setUsageLogsTotal] = useState(0);
  const [logsLoading, setLogsLoading] = useState(false);

  const cloudRequestCount = useMemo(
    () => (modelDistribution || []).reduce((sum, row) => sum + (row.request_count || 0), 0),
    [modelDistribution],
  );

  const avgCostPerRequest = useMemo(() => {
    const total = statistics?.token_usage?.total_cost_usd;
    if (!total || !cloudRequestCount) return null;
    return total / cloudRequestCount;
  }, [statistics?.token_usage?.total_cost_usd, cloudRequestCount]);

  const topChannel = useMemo(() => {
    const totals = CHANNEL_SPEND_KEYS.map(({ key, label }) => ({
      label,
      amount: (cloudSpendChart.data || []).reduce((sum, row) => sum + (row[key] || 0), 0),
    }));
    return totals.sort((a, b) => b.amount - a.amount)[0];
  }, [cloudSpendChart.data]);

  const assistantSpendRows = useMemo(
    () => (spendByAssistant || []).map((row) => ({
      model: row.assistant_name,
      assistant_id: row.assistant_id,
      total_cost_usd: row.total_cost_usd,
    })),
    [spendByAssistant],
  );

  const loadLogs = useCallback(async () => {
    if (!keycloak?.authenticated || !showCloudSpend) return;
    setLogsLoading(true);
    try {
      const result = await getUsageLogs(keycloak, {
        timeRange,
        showAll: platformFlag,
        limit: LOG_PAGE_SIZE,
        offset: (logPage - 1) * LOG_PAGE_SIZE,
        channel,
        model,
      });
      setUsageLogs(result.items || []);
      setUsageLogsTotal(result.total || 0);
    } finally {
      setLogsLoading(false);
    }
  }, [keycloak, showCloudSpend, timeRange, platformFlag, logPage, channel, model]);

  useEffect(() => {
    setLogPage(1);
  }, [timeRange, platformFlag, channel, model]);

  useEffect(() => {
    loadLogs();
  }, [loadLogs]);

  const handleSpendSeriesClick = useCallback((seriesKey) => {
    const channelMap = {
      platform_cost_usd: 'platform',
      api_cost_usd: 'api',
      bot_cost_usd: 'bot',
      widget_cost_usd: 'widget',
    };
    const channelKey = channelMap[seriesKey];
    if (channelKey) {
      navigate(`/dashboard/spend?channel=${channelKey}`);
    }
  }, [navigate]);

  if (!showCloudSpend) {
    return (
      <Card className="modern-card dashboard-spend-empty">
        <Text type="secondary">
          Cloud spend tracking appears once cloud models are enabled and usage has been logged.
        </Text>
      </Card>
    );
  }

  return (
    <div className="dashboard-spend">
      <DashboardFilterBar
        filters={[
          { key: 'channel', label: 'Channel' },
          { key: 'model', label: 'Model' },
        ]}
      />

      <Row gutter={[16, 16]} className="dashboard-spend__summary">
        <Col xs={24} sm={8}>
          <DashboardStatCard title="Total spend" loading={loading.statistics}>
            <div className="stat-value">{formatMoney(statistics?.token_usage?.total_cost_usd)}</div>
            {formatPercentageChange(statistics?.token_usage?.cost_change_percentage, { invertColors: true })}
          </DashboardStatCard>
        </Col>
        <Col xs={24} sm={8}>
          <DashboardStatCard title="Avg cost per request" loading={loading.modelDistribution}>
            <div className="stat-value">
              {avgCostPerRequest != null ? formatMoney(avgCostPerRequest) : '—'}
            </div>
          </DashboardStatCard>
        </Col>
        <Col xs={24} sm={8}>
          <DashboardStatCard title="Top channel" loading={loading.cloudSpend}>
            <div className="stat-value">{topChannel?.label || '—'}</div>
            {topChannel?.amount > 0 ? (
              <Text type="secondary" className="dashboard-spend__meta">
                {formatMoney(topChannel.amount)}
              </Text>
            ) : null}
          </DashboardStatCard>
        </Col>
      </Row>

      <Row gutter={[16, 16]} style={{ marginBottom: 20 }}>
        <Col xs={24}>
          <Card
            className="modern-card"
            title={<span className="card-title">Cloud spend over time</span>}
          >
            {loading.cloudSpend ? (
              <Skeleton active paragraph={{ rows: 8 }} />
            ) : (
              <StackedAreaChart
                height={300}
                data={cloudSpendChart.data}
                emptyState={cloudSpendChart.emptyState}
                series={CLOUD_SPEND_CHART_SERIES}
                valueKeys={CLOUD_SPEND_TIME_SERIES.valueKeys}
                flatLineKey="total_cost_usd"
                totalLabel="Total spend"
                formatValue={formatMoney}
                yAxisMin={0.0001}
                onSeriesClick={handleSpendSeriesClick}
              />
            )}
          </Card>
        </Col>
      </Row>

      <Row gutter={[16, 16]} style={{ marginBottom: 20 }}>
        <Col xs={24} lg={12}>
          <Card className="modern-card" title={<span className="card-title">Spend by model</span>}>
            {loading.modelDistribution ? (
              <Skeleton active paragraph={{ rows: 10 }} />
            ) : (
              <>
                <SpendPieChart data={modelDistribution} nameKey="model" />
                <ModelDistributionTable data={modelDistribution} loading={false} />
              </>
            )}
          </Card>
        </Col>
        <Col xs={24} lg={12}>
          <Card className="modern-card" title={<span className="card-title">Spend by assistant</span>}>
            {loading.spendByAssistant ? (
              <Skeleton active paragraph={{ rows: 10 }} />
            ) : (
              <>
                <SpendPieChart data={assistantSpendRows} nameKey="model" />
                <ModelDistributionTable
                  nameColumn="Assistant"
                  showRequests={false}
                  showTokens={false}
                  data={assistantSpendRows}
                  loading={false}
                />
              </>
            )}
          </Card>
        </Col>
      </Row>

      <Row gutter={[16, 16]}>
        <Col xs={24}>
          <Card className="modern-card" title={<span className="card-title">Usage log</span>}>
            <UsageLogTable
              data={usageLogs}
              loading={logsLoading}
              total={usageLogsTotal}
              page={logPage}
              pageSize={LOG_PAGE_SIZE}
              onPageChange={setLogPage}
            />
          </Card>
        </Col>
      </Row>
    </div>
  );
}
